import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Search } from 'lucide-react';
import Reveal from '../components/Reveal';
import { PROJECTS } from '../data/mock';

const SECTORS = ['All', ...Array.from(new Set(PROJECTS.map((p) => p.sector)))];

export default function Projects() {
  const [filter, setFilter] = useState('All');
  const [q, setQ] = useState('');

  const list = useMemo(() => {
    let l = filter === 'All' ? PROJECTS : PROJECTS.filter((p) => p.sector === filter);
    if (q) {
      const qq = q.toLowerCase();
      l = l.filter((p) => (p.title + p.location + p.summary + p.sector).toLowerCase().includes(qq));
    }
    return l;
  }, [filter, q]);

  return (
    <div className="bg-[var(--bg)]">
      <section className="pt-36 pb-16 border-b border-[var(--line)]">
        <div className="container-rs">
          <span className="section-index"><span className="coral-dot" /> 003 — Projects</span>
          <Reveal>
            <h1 className="font-display text-7xl md:text-[150px] leading-[0.92] mt-6">Selected <span className="font-display-italic text-[var(--coral)]">work.</span></h1>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-8 mt-10">
            <p className="md:col-span-6 md:col-start-7 text-[18px] text-[var(--ink-mute)] leading-relaxed">
              A selection of recently delivered schemes across residential, BtR, hospitality and commercial. Each project is delivered by a directly-employed core team with a single point of contact from sketch to handover.
            </p>
          </div>
        </div>
      </section>

      {/* Filters & search */}
      <section className="py-8 border-b border-[var(--line)] sticky top-20 lg:top-24 bg-[var(--bg)]/95 backdrop-blur z-30">
        <div className="container-rs flex flex-wrap items-center gap-3">
          <div className="flex flex-wrap gap-2">
            {SECTORS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`px-5 py-2.5 rounded-full font-tag border transition-colors ${filter === f ? 'bg-[var(--navy)] text-white border-[var(--navy)]' : 'border-[var(--line)] hover:border-[var(--navy)]'}`}>{f}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto px-4 py-2 rounded-full border border-[var(--line)] bg-white">
            <Search size={14} className="text-[var(--ink-mute)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search projects" className="bg-transparent outline-none text-sm w-44" />
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-rs grid md:grid-cols-2 gap-8 md:gap-x-10 md:gap-y-20">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={(i % 2) * 60} className={`group proj-card ${i % 2 === 1 ? 'md:translate-y-16' : ''}`}>
              <Link to={`/projects/${p.id}`} className="block">
                <div className="overflow-hidden rounded-2xl">
                  <img src={p.image} alt={p.title} className="proj-img w-full h-[440px] md:h-[560px] object-cover" />
                </div>
                <div className="flex items-start justify-between mt-6 gap-6">
                  <div>
                    <div className="font-tag text-[var(--ink-mute)]">{p.sector} · {p.role}</div>
                    <div className="font-display text-3xl md:text-4xl mt-2 group-hover:text-[var(--coral)] transition-colors">{p.title}</div>
                    <div className="text-[14px] text-[var(--ink-mute)] mt-2 max-w-md">{p.summary}</div>
                  </div>
                  <div className="font-tag text-[var(--ink-mute)] text-right whitespace-nowrap">
                    <div>{p.code}</div>
                    <div className="mt-1">{p.location}</div>
                    <div className="mt-1 text-[var(--coral)]">{p.year}</div>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
          {list.length === 0 && (
            <div className="md:col-span-2 py-24 text-center text-[var(--ink-mute)] font-tag">No projects match your search.</div>
          )}
        </div>
      </section>

      <section className="py-24 bg-[var(--navy)] text-white">
        <div className="container-rs flex flex-wrap items-center justify-between gap-10">
          <div className="font-display text-4xl md:text-6xl leading-tight max-w-2xl">Have a project in mind? <span className="font-display-italic text-[var(--coral)]">Let's talk.</span></div>
          <Link to="/contact" className="pill pill-coral"><span>Start a Project</span><ArrowUpRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
